import React, { useMemo } from 'react';

export interface M365UpdateFilters {
    createdStart: string;
    createdEnd: string;
    modifiedStart: string;
    modifiedEnd: string;
    products: string[];
    releasePhase: string[];
    platforms: string[];
    status: string[];
}

const defaultFiltersBase: Omit<M365UpdateFilters, 'releasePhase' | 'platforms' | 'status'> = {
    createdStart: '',
    createdEnd: '',
    modifiedStart: '',
    modifiedEnd: '',
    products: []
};

interface M365UpdateFilterProps {
    filters: M365UpdateFilters;
    onFiltersChange: (filters: M365UpdateFilters) => void;
    onSearch: (filters: M365UpdateFilters) => void;
    loading?: boolean;
    /** All available product names for the product filter dropdown. */
    productOptions: string[];
    /** All available release phase names (from tagsContainer.releasePhase[].tagName). */
    releasePhaseOptions: string[];
    /** Optional default selection for release phase filter (used as initial value and when Reset is clicked). */
    defaultReleasePhaseFilter?: string[];
    /** All available platform names (from tagsContainer.platforms[].tagName). */
    platformOptions: string[];
    /** All available status values (from item.status). */
    statusOptions: string[];
}

function M365UpdateFilter({
    filters,
    onFiltersChange,
    onSearch,
    loading = false,
    productOptions,
    releasePhaseOptions,
    defaultReleasePhaseFilter,
    platformOptions,
    statusOptions
}: M365UpdateFilterProps) {
    const defaultFilters = useMemo(
        (): M365UpdateFilters => ({
            ...defaultFiltersBase,
            releasePhase: defaultReleasePhaseFilter ?? [],
            platforms: [],
            status: []
        }),
        [defaultReleasePhaseFilter]
    );

    const handleChange = (field: keyof M365UpdateFilters) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const value = e.target.value;
        onFiltersChange({ ...filters, [field]: value });
    };

    const handleProductToggle = (product: string) => {
        const current = filters.products;
        const next = current.includes(product)
            ? current.filter((p) => p !== product)
            : [...current, product];
        onFiltersChange({ ...filters, products: next });
    };

    const handleReleasePhaseToggle = (phase: string) => {
        const current = filters.releasePhase;
        const next = current.includes(phase)
            ? current.filter((p) => p !== phase)
            : [...current, phase];
        onFiltersChange({ ...filters, releasePhase: next });
    };

    const handlePlatformToggle = (platform: string) => {
        const current = filters.platforms;
        const next = current.includes(platform)
            ? current.filter((p) => p !== platform)
            : [...current, platform];
        onFiltersChange({ ...filters, platforms: next });
    };

    const handleStatusToggle = (status: string) => {
        const current = filters.status;
        const next = current.includes(status)
            ? current.filter((s) => s !== status)
            : [...current, status];
        onFiltersChange({ ...filters, status: next });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSearch(filters);
    };

    const handleReset = () => {
        onFiltersChange(defaultFilters);
        onSearch(defaultFilters);
    };

    return (
        <div className="m365-update-filter card border-0 shadow-sm">
            <div className="card-body p-4">
                <h5 className="card-title mb-3">Filter updates</h5>
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label className="form-label small text-muted">Entry from (created)</label>
                        <div className="row g-2">
                            <div className="col-6">
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    placeholder="Start"
                                    value={filters.createdStart}
                                    onChange={handleChange('createdStart')}
                                    aria-label="Created from date"
                                />
                            </div>
                            <div className="col-6">
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    placeholder="End"
                                    value={filters.createdEnd}
                                    onChange={handleChange('createdEnd')}
                                    aria-label="Created to date"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label small text-muted">Last modified</label>
                        <div className="row g-2">
                            <div className="col-6">
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={filters.modifiedStart}
                                    onChange={handleChange('modifiedStart')}
                                    aria-label="Modified from date"
                                />
                            </div>
                            <div className="col-6">
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={filters.modifiedEnd}
                                    onChange={handleChange('modifiedEnd')}
                                    aria-label="Modified to date"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label small text-muted">Product</label>
                        <div className="m365-update-filter-products border rounded p-2 bg-light overflow-auto">
                            {productOptions.length === 0 ? (
                                <small className="text-muted">No products available</small>
                            ) : (
                                productOptions.map((product) => (
                                    <div key={product} className="form-check form-check-inline">
                                        <input
                                            id={`m365-product-${product.replace(/\s/g, '-')}`}
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={filters.products.includes(product)}
                                            onChange={() => handleProductToggle(product)}
                                        />
                                        <label
                                            className="form-check-label small"
                                            htmlFor={`m365-product-${product.replace(/\s/g, '-')}`}
                                        >
                                            {product}
                                        </label>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label small text-muted">Release phase</label>
                        <div className="m365-update-filter-release-phase border rounded p-2 bg-light overflow-auto">
                            {releasePhaseOptions.length === 0 ? (
                                <small className="text-muted">No release phases available</small>
                            ) : (
                                releasePhaseOptions.map((phase) => (
                                    <div key={phase} className="form-check">
                                        <input
                                            id={`m365-releasephase-${phase.replace(/\s/g, '-')}`}
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={filters.releasePhase.includes(phase)}
                                            onChange={() => handleReleasePhaseToggle(phase)}
                                        />
                                        <label
                                            className="form-check-label small"
                                            htmlFor={`m365-releasephase-${phase.replace(/\s/g, '-')}`}
                                        >
                                            {phase}
                                        </label>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="form-label small text-muted">Platforms</label>
                        <div className="m365-update-filter-platforms border rounded p-2 bg-light overflow-auto">
                            {platformOptions.length === 0 ? (
                                <small className="text-muted">No platforms available</small>
                            ) : (
                                platformOptions.map((platform) => (
                                    <div key={platform} className="form-check">
                                        <input
                                            id={`m365-platform-${platform.replace(/\s/g, '-')}`}
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={filters.platforms.includes(platform)}
                                            onChange={() => handlePlatformToggle(platform)}
                                        />
                                        <label
                                            className="form-check-label small"
                                            htmlFor={`m365-platform-${platform.replace(/\s/g, '-')}`}
                                        >
                                            {platform}
                                        </label>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="form-label small text-muted">Status</label>
                        <div className="m365-update-filter-status border rounded p-2 bg-light overflow-auto">
                            {statusOptions.length === 0 ? (
                                <small className="text-muted">No statuses available</small>
                            ) : (
                                statusOptions.map((statusVal) => (
                                    <div key={statusVal} className="form-check">
                                        <input
                                            id={`m365-status-${statusVal.replace(/\s/g, '-')}`}
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={filters.status.includes(statusVal)}
                                            onChange={() => handleStatusToggle(statusVal)}
                                        />
                                        <label
                                            className="form-check-label small"
                                            htmlFor={`m365-status-${statusVal.replace(/\s/g, '-')}`}
                                        >
                                            {statusVal}
                                        </label>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    <div className="d-flex flex-column gap-2">
                        <button
                            type="submit"
                            className="btn btn-primary btn-sm"
                            disabled={loading}
                        >
                            {loading ? 'Applying…' : 'Apply'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleReset}
                            disabled={loading}
                        >
                            Reset
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default M365UpdateFilter;
